import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { validateUserSession, loginWithCredentials } from '../services/auth';
import '../styles/Login.css';

const Login = ({ onLogin, onRegisterClick, isStandalone = false }) => {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');
  const [showNotRegistered, setShowNotRegistered] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Check for stored session on component mount
  useEffect(() => {
    const storedUserId = localStorage.getItem('userId');
    const storedToken = localStorage.getItem('token');

    if (storedUserId && storedToken) {
      validateUserSession(storedUserId, storedToken)
        .then(result => {
          if (result && result.valid) {
            onLogin(storedUserId, storedToken);
          }
        })
        .catch(error => {
          console.error("Stored session invalid:", error);
          localStorage.removeItem('userId');
          localStorage.removeItem('token');
        });
    }
  }, [onLogin]);

  const handleLogin = async (e) => {
    e.preventDefault();

    // Fall back to DOM values if React state is empty (browser autofill doesn't always fire onChange)
    const usernameEl = document.getElementById('username');
    const passwordEl = document.getElementById('password');
    const finalUsername = username || (usernameEl && usernameEl.value) || '';
    const finalPassword = password || (passwordEl && passwordEl.value) || '';

    if (finalUsername) setUsername(finalUsername);
    if (finalPassword) setPassword(finalPassword);

    if (!finalUsername.trim() || !finalPassword) {
      setErrorMessage('Please enter both username and password');
      return;
    }

    if (isLoggingIn) return;

    setIsLoggingIn(true);
    setErrorMessage('');

    try {
      const result = await loginWithCredentials(finalUsername.trim(), finalPassword);

      if (result && result.userId && result.token) {
        localStorage.setItem('userId', result.userId);
        localStorage.setItem('token', result.token);

        if (onLogin) {
          onLogin(result.userId, result.token);
        }
      } else {
        throw new Error('Invalid login response');
      }
    } catch (error) {
      console.error("Login error:", error);
      const message = error.message || 'Login failed. Please check your credentials.';
      setErrorMessage(message);

      // If user not found, show registration prompt
      if (message.toLowerCase().includes('not found') || message.toLowerCase().includes('no user')) {
        setShowNotRegistered(true);
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleGoToRegistration = () => {
    navigate('/register');
  };

  return (
    <div className="login-terminal">
      {/* Scanlines overlay */}
      <div className="terminal-scanlines"></div>

      {/* Header */}
      <div className="terminal-header">
        <div className="terminal-logo">KOMOK</div>
        <div className="terminal-subtitle">EMPLOYEE ACCESS TERMINAL</div>
      </div>

      {/* Terminal window */}
      <div className="terminal-window">
        <div className="terminal-title-bar">
          <span className="terminal-title">AUTHENTICATION REQUIRED</span>
          <div className="terminal-dots">
            <span className="dot red"></span>
            <span className="dot yellow"></span>
            <span className="dot green"></span>
          </div>
        </div>

        <div className="terminal-content">
          {/* Error message */}
          {errorMessage && (
            <div className="terminal-error">
              <span className="error-icon">!</span>
              <span className="error-text">{errorMessage}</span>
              <button onClick={() => setErrorMessage('')} className="error-dismiss">×</button>
            </div>
          )}

          {/* Login form */}
          <form onSubmit={handleLogin} className="login-form">
            <div className="input-group">
              <label htmlFor="username">
                <span className="label-icon">{'>'}</span>
                USERNAME
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter employee username"
                disabled={isLoggingIn}
                autoComplete="username"
                autoFocus
              />
            </div>

            <div className="input-group">
              <label htmlFor="password">
                <span className="label-icon">{'>'}</span>
                PASSWORD
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter access code"
                  disabled={isLoggingIn}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? '◉' : '○'}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className={`login-button ${isLoggingIn ? 'loading' : ''}`}
              disabled={isLoggingIn}
            >
              {isLoggingIn ? (
                <>
                  <span className="loading-spinner"></span>
                  AUTHENTICATING...
                </>
              ) : (
                'LOGIN'
              )}
            </button>
          </form>

          {/* Not registered prompt */}
          {showNotRegistered && (
            <div className="not-registered-prompt">
              <p>Account not found. Need to register?</p>
              <button onClick={handleGoToRegistration} className="go-register-btn">
                GO TO REGISTRATION
              </button>
            </div>
          )}

          {/* Registration links */}
          <div className="terminal-footer">
            <div className="footer-line"></div>
            <p className="footer-text">
              First time? Register with a code or scan a QR.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              <button
                className="register-link"
                onClick={() => navigate('/register')}
                disabled={isLoggingIn}
              >
                ENTER CODE →
              </button>
              {onRegisterClick && (
                <button
                  className="register-link"
                  onClick={onRegisterClick}
                  disabled={isLoggingIn}
                >
                  SCAN QR →
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick links */}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '20px', zIndex: 1 }}>
        <button className="register-link" onClick={() => navigate('/home')}>HOME</button>
        <button className="register-link" onClick={() => navigate('/market')}>MARKET</button>
        <button className="register-link" onClick={() => navigate('/leaderboard')}>LEADERBOARD</button>
        <button className="register-link" onClick={() => navigate('/videos')}>VIDEOS</button>
      </div>

      {/* Bottom tagline */}
      <div className="terminal-tagline">
        "Your productivity is our profit"
      </div>
    </div>
  );
};

export default Login;
